import * as yup from 'yup';
import { EarmarkDTO } from '../../OtherAssets.meta';
import { BusinessAssetDTO, BusinessSaleScheduleDTO } from '../../tables/Business/Business.meta';
import {
  EXCLUDE_OPT,
  getCustomBucketIndex,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';

export interface BusinessAssetsFormValues {
  name: string;
  owner: string | null;
  value: number | null;
  yearlyIncome: number | null;
  assetGrowth: number | null;
  saleYear: string | null;
  incomeGrowth: number | null;
  assetEarmarks: { value: string; label: string }[];
  selfEmployed: boolean;
  BusinessSaleSchedule: BusinessSaleScheduleDTO[];
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
}

export interface BusinessAssetsFormDTO {
  Name: string;
  Owner: string;
  Value: number | null;
  YearlyIncome: number | null;
  AssetGrowth: number | null;
  SaleYear: string | null;
  IsSoldAtRetirement: boolean;
  IncomeGrowth: number | null;
  AssetEarmark: EarmarkDTO[];
  SelfEmployed: boolean;
  BusinessSaleSchedule: BusinessSaleScheduleDTO[];
  CustomBucketIndex: number | null;
  IsExcludedFromAssetBucketing: boolean;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
  OrionManagementStyleId: number | null;
}

export interface UpsertBusinessAssetDTO extends BusinessAssetsFormDTO {
  clientId: string;
  Id: string | null;
}

interface DataToForm {
  data: BusinessAssetDTO | null;
  options: OtherAssetsDTO['Options'];
}

export const dataToForm = ({ data, options }: DataToForm): BusinessAssetsFormValues =>
  data
    ? {
        name: data.Name,
        owner: data.Owner,
        value: data.Value,
        yearlyIncome: data.YearlyIncome,
        assetGrowth: data.AssetGrowth,
        saleYear:
          data.SaleYear !== null
            ? data.SaleYear.toString()
            : data.IsSoldAtRetirement
            ? 'At Retirement'
            : null,
        incomeGrowth: data.IncomeGrowth,
        assetEarmarks: data.AssetEarmark.map(({ Type, Tooltip }) => ({
          value: Type,
          label: Tooltip,
        })),
        selfEmployed: data.SelfEmployed || false,
        BusinessSaleSchedule: data.BusinessSaleSchedule,
        customBucketIndex: getCustomBucketIndex({
          orionManagementStyleId: data.OrionManagementStyleId,
          customBucketIndex: data.CustomBucketIndex,
          managementStyles: options.ManagementStyles,
          isExcludedFromAssetBucketing: false,
          assetBucketingByManagementStyle: options.AssetBucketingByManagementStyle,
        }),
        isLiquid: data.IsLiquid,
        includeInGrossEstate: data.IncludeInGrossEstate,
        orionManagementStyleId: data.OrionManagementStyleId,
      }
    : {
        name: '',
        owner: '',
        value: null,
        yearlyIncome: null,
        assetGrowth: null,
        saleYear: null,
        incomeGrowth: null,
        assetEarmarks: [],
        selfEmployed: false,
        BusinessSaleSchedule: [
          {
            YearsAfterSaleYear: 0,
            SaleAmount: null,
            NetWorthPercent: null,
          },
        ],
        customBucketIndex: null,
        isLiquid: options.BusinessAssetOptions.IsLiquid,
        includeInGrossEstate: true,
        orionManagementStyleId: null,
      };

export const formToBody = (values: BusinessAssetsFormValues): BusinessAssetsFormDTO => ({
  Name: values.name,
  Owner: values.owner || '',
  Value: values.value,
  YearlyIncome: values.yearlyIncome,
  AssetGrowth: values.assetGrowth === null ? 0 : values.assetGrowth,
  SaleYear: values.saleYear === 'At Retirement' ? null : values.saleYear,
  IsSoldAtRetirement: values.saleYear === 'At Retirement',
  IncomeGrowth: values.incomeGrowth,
  AssetEarmark: values.assetEarmarks.map(({ value, label }) => ({ Type: value, Tooltip: label })),
  SelfEmployed: values.selfEmployed,
  BusinessSaleSchedule: values.BusinessSaleSchedule,
  CustomBucketIndex:
    values.customBucketIndex === EXCLUDE_OPT.value ? null : values.customBucketIndex,
  IsExcludedFromAssetBucketing: values.customBucketIndex === EXCLUDE_OPT.value,
  IsLiquid: values.isLiquid,
  IncludeInGrossEstate: values.includeInGrossEstate,
  OrionManagementStyleId: values.orionManagementStyleId,
});

export const FORM_SCHEMA = yup.object().shape({
  name: yup.string().required('Please specify a name'),
  owner: yup.string().required('Please specify an owner'),
  value: yup
    .number()
    .nullable()
    .notOneOf([0], 'Please specify a valid value for this business')
    .required('Please provide a value for this business'),
});
